// hooks/usePermissions.ts
import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  hasRole,
  hasTournamentRole,
  isAdmin,
  isStoreOwner,
  isStaff,
  hasStaffAccess,
  isManager,
  isHeadJudge,
  hasMinimumRole,
  hasPermission,
  hasAllPermissions,
  hasAnyPermission,
  getUserPermissions,
  PERMISSIONS,
  UserWithRole,
  Permission,
} from '../utils/permissions';
import type { RoleValue } from '../config/constants';
import type { AccountType, TournamentRole } from '../types/roles.types';

// Types
export interface UsePermissionsResult {
  // Account type checks (global)
  isAdmin: boolean;
  isStoreOwner: boolean;
  isStaff: boolean;
  hasStaffAccess: boolean;
  isPermanentManager: boolean;

  // Tournament role checks
  isManager: boolean;
  isHeadJudge: boolean;

  // All permissions for current user
  allPermissions: Permission[];

  // Current account type and legacy role
  accountType: AccountType | undefined;
  role: RoleValue | undefined;
  tournamentRole: TournamentRole | undefined;
  permanentStoreId: string | undefined;
  permanentStoreRole: string | undefined;
  permanentStoreName: string | undefined;

  // Convenience checks for common permissions
  canCreateTournament: boolean;
  canEditTournament: boolean;
  canArchiveTournament: boolean;
  canViewTables: boolean;
  canEditTableResult: boolean;
  canViewPlayers: boolean;
  canUploadPlayers: boolean;
  canIssuePenalty: boolean;
  canApprovePenalty: boolean;
  canViewAppeals: boolean;
  canTakeAppeal: boolean;
  canConcludeAppeal: boolean;
  canViewAppealLog: boolean;
  canManageStaff: boolean;
  canControlTimer: boolean;
  canApproveUsers: boolean;
  canViewAdminDashboard: boolean;

  // Functions
  hasRole: (roles: RoleValue | RoleValue[]) => boolean;
  hasTournamentRole: (roles: TournamentRole | TournamentRole[]) => boolean;
  hasMinimumRole: (role: RoleValue) => boolean;
  hasPermission: (permission: Permission) => boolean;
  hasAllPermissions: (perms: Permission[]) => boolean;
  hasAnyPermission: (perms: Permission[]) => boolean;

  // Other
  loading: boolean;
  user: UserWithRole | null;
  PERMISSIONS: typeof PERMISSIONS;
}

/**
 * usePermissions - Hook for checking user permissions
 * Supports new accountType system and legacy role system
 */
export function usePermissions(): UsePermissionsResult {
  const { user, loading } = useAuth() as { user: UserWithRole | null; loading: boolean };

  // Memoized permission checks
  const permissions = useMemo(() => {
    // Check if user is a permanent manager for a store
    const permanentStoreId = (user as { permanentStoreId?: string })?.permanentStoreId;
    const permanentStoreRole = (user as { permanentStoreRole?: string })?.permanentStoreRole;
    const permanentStoreName = (user as { permanentStoreName?: string })?.permanentStoreName;
    const isPermanentManager = Boolean(permanentStoreId && permanentStoreRole === 'Manager');

    // Compute the combined permission set ONCE (getCombinedPermissions is not
    // cheap) instead of letting each hasPermission() call below rebuild it.
    // [] when there's no user — matches hasPermission()'s null short-circuit.
    const allPermissions = getUserPermissions(user);
    const has = (permission: Permission): boolean => allPermissions.includes(permission);

    return {
      // Account type checks (global)
      isAdmin: isAdmin(user),
      isStoreOwner: isStoreOwner(user),
      isStaff: isStaff(user),
      hasStaffAccess: hasStaffAccess(user),
      isPermanentManager,

      // Tournament role checks
      isManager: isManager(user),
      isHeadJudge: isHeadJudge(user),

      // All permissions for current user
      allPermissions,

      // Current account type and roles
      accountType: user?.accountType as AccountType | undefined,
      role: user?.role,
      tournamentRole: user?.tournamentRole,
      permanentStoreId,
      permanentStoreRole,
      permanentStoreName,

      // Convenience checks for common permissions
      canCreateTournament: has(PERMISSIONS.CREATE_TOURNAMENT),
      canEditTournament: has(PERMISSIONS.EDIT_TOURNAMENT),
      canArchiveTournament: has(PERMISSIONS.ARCHIVE_TOURNAMENT),
      canViewTables: has(PERMISSIONS.VIEW_TABLES),
      canEditTableResult: has(PERMISSIONS.EDIT_TABLE_RESULT),
      canViewPlayers: has(PERMISSIONS.VIEW_PLAYERS),
      canUploadPlayers: has(PERMISSIONS.UPLOAD_PLAYERS),
      canIssuePenalty: has(PERMISSIONS.ISSUE_PENALTY),
      canApprovePenalty: has(PERMISSIONS.APPROVE_PENALTY),
      canViewAppeals: has(PERMISSIONS.VIEW_APPEALS),
      canTakeAppeal: has(PERMISSIONS.TAKE_APPEAL),
      canConcludeAppeal: has(PERMISSIONS.CONCLUDE_APPEAL),
      canViewAppealLog: has(PERMISSIONS.VIEW_APPEAL_LOG),
      canManageStaff: has(PERMISSIONS.MANAGE_STAFF),
      canControlTimer: has(PERMISSIONS.CONTROL_TIMER),
      canApproveUsers: has(PERMISSIONS.APPROVE_USERS),
      canViewAdminDashboard: has(PERMISSIONS.VIEW_ADMIN_DASHBOARD),
    };
  }, [user]);

  // Functions that need to be called with parameters
  const checkRole = useMemo(() => ({
    hasRole: (roles: RoleValue | RoleValue[]) => hasRole(user, roles),
    hasTournamentRole: (roles: TournamentRole | TournamentRole[]) => hasTournamentRole(user, roles),
    hasMinimumRole: (role: RoleValue) => hasMinimumRole(user, role),
    hasPermission: (permission: Permission) => hasPermission(user, permission),
    hasAllPermissions: (perms: Permission[]) => hasAllPermissions(user, perms),
    hasAnyPermission: (perms: Permission[]) => hasAnyPermission(user, perms),
  }), [user]);

  return {
    ...permissions,
    ...checkRole,
    loading,
    user,
    PERMISSIONS,
  };
}

/**
 * useRequirePermission - Hook that checks if user has required permission(s)
 */
export function useRequirePermission(requiredPermissions: Permission | Permission[]): boolean {
  const { user } = useAuth() as { user: UserWithRole | null };

  const perms = Array.isArray(requiredPermissions)
    ? requiredPermissions
    : [requiredPermissions];

  return hasAllPermissions(user, perms);
}

/**
 * useRequireRole - Hook to check if user has required role
 */
export function useRequireRole(roles: RoleValue | RoleValue[]): boolean {
  const { user } = useAuth() as { user: UserWithRole | null };
  return hasRole(user, roles);
}

export default usePermissions;
